import { Subject, of, forkJoin, firstValueFrom } from 'rxjs'
import { takeUntil, catchError, filter, take, timeout } from 'rxjs/operators'

import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'

import { AuthenticationService } from '@services/authentication.service'
import { AvailableBatch, OpsLaborEntry, OpsService, OpsStation, OpsStationConnection } from '@services/ops.service'
import { OEEDataService } from 'src/app/features/oee-dashboard/services/oee-data.service'
import { TimeEntrySanityCheckModalComponent, TimeEntrySanityCheckModalData, TimeEntrySanityCheckModalResult } from '../../shared/time-entry-sanity-check-modal/time-entry-sanity-check-modal.component'
import { buildJobRoutingContext, evaluateSanity, hasSanityWarnings } from '../../shared/time-entry-sanity-check-modal/time-entry-sanity'
import { submitWithReworkPrompt } from '../rework-decision-modal/rework-decision.helper'

export interface DuplicateShiftTemplate {
    id: string
    mode: 'job' | 'activity'
    sourceDateLabel: string
    title: string
    subtitle: string
    batchEventId?: string | null
    batchNumber?: string | null
    equipmentId?: string | null
    notes?: string | null
    activityTag?: string | null
    clockInTs: string
    clockOutTs?: string | null
}

export interface AddShiftModalData {
    siteId: string
    dateKey: string
    initialMode?: 'job' | 'activity'
    initialActivity?: string
    initialStartTime?: string
    initialEndTime?: string
    duplicateTemplates?: DuplicateShiftTemplate[]
    /** Operator's rolling last-30-days labor entries — used by the unusual-station warning */
    stationHistory?: OpsLaborEntry[]
}

export interface AddShiftModalResult {
    created: boolean
}

export const MY_SHIFTS_ACTIVITY_CATEGORIES = [
    { name: 'Break', icon: 'fa-solid fa-mug-hot' },
    { name: 'Code Convert', icon: 'fa-solid fa-code' },
    { name: 'External Support', icon: 'fa-solid fa-headset' },
    { name: 'Maintenance', icon: 'fa-solid fa-wrench' },
    { name: 'Meeting', icon: 'fa-solid fa-comments' },
    { name: 'Training', icon: 'fa-solid fa-graduation-cap' },
    { name: 'Waiting', icon: 'fa-solid fa-boxes-stacked' },
    { name: '6S Time', icon: 'fa-solid fa-broom' },
    { name: 'Lunch', icon: 'fa-solid fa-utensils' }
] as const

@Component({
    selector: 'app-add-shift-modal',
    templateUrl: './add-shift-modal.component.html',
    styleUrls: ['./add-shift-modal.component.scss'],
    standalone: false
})
export class AddShiftModalComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>()
    private readonly lastStationStorageKeyPrefix = 'my_shifts_last_add_time_station_id'
    private readonly submitAuthWaitMs = 1500

    batches: AvailableBatch[] = []
    stations: OpsStation[] = []
    private stationConnections: OpsStationConnection[] = []
    private connectedStationIds = new Set<string>()
    selectedBatches: AvailableBatch[] = []

    startDateObj: Date | null = null
    endDateObj: Date | null = null

    mode: 'job' | 'activity' = 'job'
    jobEntryMode: 'single' | 'multiple' = 'single'
    selectedStationId: string | null = null
    searchQuery = ''
    selectedDuplicateTemplateId: string | null = null

    activityCategories = MY_SHIFTS_ACTIVITY_CATEGORIES
    selectedCategory = ''
    notes = ''

    // "Waiting on Parts" optional job picker
    waitingOnPartsBatches: AvailableBatch[] = []
    waitingOnPartsSearchQuery = ''
    waitingOnPartsStationId: string | null = null

    loading = true
    submitting = false
    error = ''

    constructor(
        private _dialogRef: MatDialogRef<AddShiftModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AddShiftModalData,
        private _opsService: OpsService,
        private _oeeDataService: OEEDataService,
        private _authService: AuthenticationService,
        private _cdr: ChangeDetectorRef,
        private _dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.mode = this.data.initialMode ?? 'job'
        if (this.data.initialActivity) {
            this.selectedCategory = this.data.initialActivity
        }
        this.initDefaultStartTime()
        if (this.data.initialStartTime) {
            this.startDateObj = new Date(this.data.initialStartTime)
        }
        if (this.data.initialEndTime) {
            this.endDateObj = new Date(this.data.initialEndTime)
        }
        this.loadData()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    get selectedStation(): OpsStation | null {
        return this.stations.find(station => station.id === this.selectedStationId) ?? null
    }

    get filteredBatches(): AvailableBatch[] {
        const query = this.searchQuery.trim().toLowerCase()
        const batches = [...this.batches].sort((left, right) => {
            const leftTier = this.getBatchConnectionTier(left)
            const rightTier = this.getBatchConnectionTier(right)
            if (leftTier !== rightTier) {
                return leftTier - rightTier
            }

            const timeEntryDelta = this.getBatchSortTime(right.latest_time_entry_ts) - this.getBatchSortTime(left.latest_time_entry_ts)
            if (timeEntryDelta !== 0) {
                return timeEntryDelta
            }

            const startedAtDelta = this.getBatchSortTime(right.started_at) - this.getBatchSortTime(left.started_at)
            if (startedAtDelta !== 0) {
                return startedAtDelta
            }

            return left.batch_number.localeCompare(right.batch_number, undefined, { numeric: true, sensitivity: 'base' })
        })

        if (!query) {
            return batches
        }

        return batches.filter(batch =>
            batch.batch_number.toLowerCase().includes(query)
            || (batch.work_order?.toLowerCase().includes(query) ?? false)
            || batch.equipment_name.toLowerCase().includes(query)
            || (batch.product_name?.toLowerCase().includes(query) ?? false)
        )
    }

    private getBatchSortTime(value?: string | null): number {
        if (!value) {
            return 0
        }

        const parsed = Date.parse(value)
        return Number.isNaN(parsed) ? 0 : parsed
    }

    getBatchSubtitle(batch: AvailableBatch): string {
        const parts: string[] = []

        if (batch.product_name) {
            parts.push(batch.product_name)
            if (batch.equipment_name) {
                parts.push(`Last at ${batch.equipment_name}`)
            }
        } else if (batch.equipment_name) {
            parts.push(batch.equipment_name)
        }

        if (batch.work_order) {
            parts.push(`WO ${batch.work_order}`)
        }

        return parts.join(' \u00b7 ')
    }

    /**
     * Pill label / tooltip for a recently-closed batch surfaced by the 7-day
     * recovery arm of get_available_batches_for_site. Returns null for active
     * batches so the template can guard with @if (#3160).
     */
    getRecentlyClosedPill(batch: AvailableBatch): { label: string, tooltip: string } | null {
        const reason = batch.recently_closed_reason
        if (!reason) {
            return null
        }
        const labels: Record<NonNullable<typeof reason>, string> = {
            resolved: 'Recently resolved',
            archived: 'Recently archived',
            aborted: 'Recently aborted',
            completed: 'Recently completed'
        }
        return {
            label: labels[reason],
            tooltip: 'This job was closed. Selecting it will reopen it for your shift.'
        }
    }

    get duplicateTemplates(): DuplicateShiftTemplate[] {
        return this.data.duplicateTemplates ?? []
    }

    get totalSelectedCount(): number {
        return this.selectedBatches.length
    }

    get isMultipleJobMode(): boolean {
        return this.mode === 'job' && this.jobEntryMode === 'multiple'
    }

    get totalMinutes(): number | null {
        const start = this.asDate(this.startDateObj)
        const end = this.asDate(this.endDateObj)

        if (!start || !end) {
            return null
        }

        const diffMs = end.getTime() - start.getTime()
        if (diffMs <= 0) {
            return null
        }

        return Math.round(diffMs / 60000)
    }

    get minutesPerSelectedRma(): number | null {
        if (!this.totalMinutes || this.selectedBatches.length === 0) {
            return null
        }

        return Math.floor(this.totalMinutes / this.selectedBatches.length)
    }

    get submitLabel(): string {
        if (this.mode === 'activity') {
            return 'Add Entry'
        }

        const count = this.selectedBatches.length
        if (!this.isMultipleJobMode || count <= 1) {
            return this.endDateObj ? 'Add Entry' : 'Start Entry'
        }

        return this.endDateObj ? `Add Entry (${count} Jobs)` : `Start Entry (${count} Jobs)`
    }

    get canSubmit(): boolean {
        const start = this.asDate(this.startDateObj)
        const end = this.asDate(this.endDateObj)

        if (!start || this.submitting) {
            return false
        }

        if (start && end && end <= start) {
            return false
        }

        if (this.mode === 'activity') {
            return !!this.selectedCategory
        }

        return !!this.selectedStationId
            && this.selectedBatches.length > 0
            && (!this.isMultipleJobMode || this.selectedBatches.length > 1)
    }

    get dateDisplay(): string {
        const [y, m, d] = this.data.dateKey.split('-').map(Number)
        const date = new Date(y, m - 1, d)
        return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
    }

    get startPickerInitialDate(): Date | null {
        return this.asDate(this.startDateObj)
    }

    get endPickerInitialDate(): Date | null {
        const end = this.asDate(this.endDateObj)
        if (end) {
            return end
        }

        const start = this.asDate(this.startDateObj)
        if (!start) {
            return null
        }

        return new Date(start.getFullYear(), start.getMonth(), start.getDate())
    }

    private initDefaultStartTime(): void {
        const now = new Date()
        const todayKey = this.toLocalDateString(now)
        const [y, mo, d] = this.data.dateKey.split('-').map(Number)

        if (this.data.dateKey < todayKey) {
            this.startDateObj = new Date(y, mo - 1, d, 8, 0, 0)
        } else {
            this.startDateObj = this.roundToNearestQuarterHour(
                new Date(y, mo - 1, d, now.getHours(), now.getMinutes(), 0)
            )
        }
    }

    private roundToNearestQuarterHour(date: Date): Date {
        const rounded = new Date(date)
        const roundedMinutes = Math.round(rounded.getMinutes() / 15) * 15
        rounded.setMinutes(roundedMinutes, 0, 0)
        return rounded
    }

    private loadData(): void {
        this.loading = true

        forkJoin({
            batches: this._opsService.getAvailableBatches(this.data.siteId).pipe(
                catchError(() => of([] as AvailableBatch[]))
            ),
            stations: this._opsService.getStations(this.data.siteId).pipe(
                catchError(() => of([] as OpsStation[]))
            ),
            connections: this._opsService.getStationConnections(this.data.siteId).pipe(
                catchError(() => of([] as OpsStationConnection[]))
            )
        }).pipe(
            takeUntil(this.destroy$)
        ).subscribe(({ batches, stations, connections }) => {
            this.batches = batches
            this.stations = stations.filter(station => !station.is_archived && station.allows_batch_operators !== false)
            this.stationConnections = connections
            this.selectedStationId = this.resolveInitialStationId()
            this.waitingOnPartsStationId = this.selectedStationId
            this.rebuildConnectedStationIds()
            this.applyInitialDuplicateTemplate()
            this.loading = false
            this._cdr.detectChanges()
        })
    }

    applyDuplicateTemplate(template: DuplicateShiftTemplate): void {
        this.selectedDuplicateTemplateId = template.id
        this.error = ''

        this.mode = template.mode
        this.jobEntryMode = 'single'
        this.searchQuery = ''
        this.selectedBatches = []
        this.selectedCategory = ''
        this.notes = ''
        this.endDateObj = null
        this.waitingOnPartsBatches = []
        this.waitingOnPartsSearchQuery = ''
        this.waitingOnPartsStationId = null

        if (template.mode === 'activity') {
            this.selectedCategory = template.activityTag && this.activityCategories.some(cat => cat.name === template.activityTag)
                ? template.activityTag
                : ''
            this.notes = template.notes ?? ''
            return
        }

        const matchingBatch = this.findMatchingBatchForTemplate(template)

        const templateEquipmentIsKnown = !!template.equipmentId
            && this.stations.some(station => station.id === template.equipmentId)

        if (!matchingBatch && !templateEquipmentIsKnown) {
            this.error = 'The selected job is no longer available to resume.'
            return
        }

        // The resumed entry's own station wins over the matched batch's equipment, because
        // get_available_batches dedupes by batch_number and keeps only the newest machine
        // event — which may sit at a different station than where the original entry was logged.
        this.selectedStationId = templateEquipmentIsKnown
            ? template.equipmentId!
            : matchingBatch!.equipment_id
        this.persistLastStationPreference(this.selectedStationId)
        this.selectedBatches = matchingBatch ? [matchingBatch] : []
    }

    private findMatchingBatchForTemplate(template: DuplicateShiftTemplate): AvailableBatch | undefined {
        if (template.batchEventId) {
            const byEventId = this.batches.find(batch => batch.batch_event_id === template.batchEventId)
            if (byEventId) {
                return byEventId
            }
        }

        if (template.batchNumber && template.equipmentId) {
            const byBatchAndStation = this.batches.find(batch =>
                batch.batch_number === template.batchNumber
                && batch.equipment_id === template.equipmentId
            )
            if (byBatchAndStation) {
                return byBatchAndStation
            }
        }

        if (template.batchNumber) {
            return this.batches.find(batch => batch.batch_number === template.batchNumber)
        }

        return undefined
    }

    setMode(newMode: 'job' | 'activity'): void {
        if (newMode === this.mode) return
        this.mode = newMode
        this.error = ''

        if (newMode === 'activity') {
            this.selectedBatches = []
        } else {
            this.selectedCategory = ''
            this.waitingOnPartsBatches = []
            this.waitingOnPartsSearchQuery = ''
            this.waitingOnPartsStationId = null
        }
    }

    setJobEntryMode(newMode: 'single' | 'multiple'): void {
        if (newMode === this.jobEntryMode) {
            return
        }

        this.jobEntryMode = newMode
        this.error = ''

        if (newMode === 'single' && this.selectedBatches.length > 1) {
            this.selectedBatches = this.selectedBatches.slice(0, 1)
        }
    }

    onStationChange(): void {
        this.error = ''
        this.persistLastStationPreference(this.selectedStationId)
        this.rebuildConnectedStationIds()
    }

    private getBatchConnectionTier(batch: AvailableBatch): number {
        if (!this.selectedStationId) return 0
        if (batch.equipment_id === this.selectedStationId) return 0
        if (this.connectedStationIds.has(batch.equipment_id)) return 1
        return 2
    }

    private rebuildConnectedStationIds(): void {
        this.connectedStationIds = new Set<string>()
        if (!this.selectedStationId) return
        for (const conn of this.stationConnections) {
            if (conn.from_station_id === this.selectedStationId) {
                this.connectedStationIds.add(conn.to_station_id)
            }
            if (conn.to_station_id === this.selectedStationId) {
                this.connectedStationIds.add(conn.from_station_id)
            }
        }
    }

    isBatchSelected(batch: AvailableBatch): boolean {
        return this.selectedBatches.some(selected => selected.batch_event_id === batch.batch_event_id)
    }

    toggleBatchSelection(batch: AvailableBatch): void {
        if (this.jobEntryMode === 'single') {
            this.selectedBatches = this.isBatchSelected(batch) ? [] : [batch]
            return
        }

        if (this.isBatchSelected(batch)) {
            this.selectedBatches = this.selectedBatches.filter(selected => selected.batch_event_id !== batch.batch_event_id)
            return
        }

        this.selectedBatches = [...this.selectedBatches, batch]
    }

    moveSelectedBatch(index: number, direction: -1 | 1): void {
        const targetIndex = index + direction
        if (targetIndex < 0 || targetIndex >= this.selectedBatches.length) {
            return
        }

        const reordered = [...this.selectedBatches]
        const [moved] = reordered.splice(index, 1)
        reordered.splice(targetIndex, 0, moved)
        this.selectedBatches = reordered
    }

    removeSelectedBatch(index: number): void {
        this.selectedBatches = this.selectedBatches.filter((_, currentIndex) => currentIndex !== index)
    }

    selectCategory(name: string): void {
        if (this.selectedCategory === name) {
            this.selectedCategory = ''
            this.waitingOnPartsBatches = []
            this.waitingOnPartsSearchQuery = ''
            this.waitingOnPartsStationId = null
            return
        }

        this.selectedCategory = name

        // Clear the waiting-on-parts picker when switching away
        if (name !== 'Waiting') {
            this.waitingOnPartsBatches = []
            this.waitingOnPartsSearchQuery = ''
            this.waitingOnPartsStationId = null
        }
    }

    get isWaitingOnPartsSelected(): boolean {
        return this.mode === 'activity' && this.selectedCategory === 'Waiting'
    }

    get filteredWaitingOnPartsBatches(): AvailableBatch[] {
        let results = this.batches

        if (this.waitingOnPartsStationId) {
            results = results.filter(b => b.equipment_id === this.waitingOnPartsStationId)
        }

        const query = this.waitingOnPartsSearchQuery.trim().toLowerCase()
        if (query) {
            results = results.filter(batch =>
                batch.batch_number.toLowerCase().includes(query)
                || (batch.work_order?.toLowerCase().includes(query) ?? false)
                || batch.equipment_name.toLowerCase().includes(query)
                || (batch.product_name?.toLowerCase().includes(query) ?? false)
            )
        }

        return results.slice(0, 20)
    }

    isWaitingBatchSelected(batch: AvailableBatch): boolean {
        return this.waitingOnPartsBatches.some(b => b.batch_event_id === batch.batch_event_id)
    }

    toggleWaitingOnPartsBatch(batch: AvailableBatch): void {
        if (this.isWaitingBatchSelected(batch)) {
            this.waitingOnPartsBatches = this.waitingOnPartsBatches.filter(b => b.batch_event_id !== batch.batch_event_id)
        } else {
            this.waitingOnPartsBatches = [...this.waitingOnPartsBatches, batch]
        }
    }

    async onSubmit(): Promise<void> {
        if (!this.canSubmit) {
            this.error = 'Complete the required fields before starting this entry.'
            this._cdr.detectChanges()
            return
        }

        this.submitting = true
        this.error = ''
        this._cdr.detectChanges()

        try {
            const userId = await this.resolveSubmitUserId()
            if (!userId) {
                this.error = 'We could not confirm your signed-in user. Refresh the page and try again.'
                return
            }

            const proceed = await this.runSanityCheck()
            if (!proceed) {
                this.error = 'Time entry was not started. Confirm the warning prompt to continue.'
                return
            }

            if (this.mode === 'activity') {
                await this.submitActivityTime(userId)
            } else {
                await this.submitJobTime(userId)
            }

            this._dialogRef.close({ created: true } as AddShiftModalResult)
        } catch (err) {
            if (!this.error) {
                this.error = this.extractApiErrorMessage(err, 'Failed to create time entry')
            }
        } finally {
            this.submitting = false
            this._cdr.detectChanges()
        }
    }

    private async resolveSubmitUserId(): Promise<string | null> {
        const currentUserId = this._authService.currentUser?.sub
        if (currentUserId) return currentUserId

        try {
            const user = await firstValueFrom(this._authService.user$.pipe(
                filter(user => !!user?.sub),
                take(1),
                timeout({ first: this.submitAuthWaitMs, with: () => of(null) })
            ))
            return user?.sub ?? null
        } catch {
            return null
        }
    }

    /**
     * Surface multi-day / unusual-station / routing warnings before submit. Returns true if
     * the user wants to proceed (or there were no warnings to begin with).
     */
    private async runSanityCheck(): Promise<boolean> {
        const start = this.asDate(this.startDateObj)
        const end = this.asDate(this.endDateObj)
        if (!start) return true

        // Station only applies to job entries; activity entries are submitted with station_id=null.
        const isJob = this.mode === 'job'
        // KNOWN GAP: routing checks (off-process-flow + job-active-elsewhere) only fire in
        // single-job mode. In multiple-jobs mode there are N batches against one station —
        // surfacing per-batch warnings would require aggregation that doesn't fit the
        // one-warning-per-reason modal pattern. The multi-day / unusual-station checks still fire.
        const selectedSingleBatch = isJob && !this.isMultipleJobMode && this.selectedBatches.length === 1
            ? this.selectedBatches[0]
            : null
        const candidate = {
            id: null,
            clockInIso: start.toISOString(),
            clockOutIso: end ? end.toISOString() : null,
            stationId: isJob ? this.selectedStationId : null,
            stationName: isJob ? (this.selectedStation?.station_name ?? null) : null,
            batchNumber: selectedSingleBatch?.batch_number ?? null,
            productName: selectedSingleBatch?.product_name ?? null,
            stationAllowsConcurrent: isJob ? (this.selectedStation?.allow_concurrent_jobs ?? false) : false
        }
        const routing = selectedSingleBatch
            ? buildJobRoutingContext(this.batches, this.stationConnections, selectedSingleBatch.batch_number)
            : undefined
        const result = evaluateSanity(candidate, this.data.stationHistory ?? [], routing)
        if (!hasSanityWarnings(result)) return true

        const ref = this._dialog.open<TimeEntrySanityCheckModalComponent, TimeEntrySanityCheckModalData, TimeEntrySanityCheckModalResult>(
            TimeEntrySanityCheckModalComponent,
            {
                width: '450px',
                maxWidth: '95vw',
                panelClass: 'oee-mobile-modal',
                data: { ...result, action: 'add' }
            }
        )
        // MatDialog#afterClosed always emits exactly once on close; no need to wrap in takeUntil.
        const res = await firstValueFrom(ref.afterClosed())
        return !!res?.proceed
    }

    private async submitActivityTime(userId: string): Promise<void> {
        const start = this.asDate(this.startDateObj)
        const end = this.asDate(this.endDateObj)

        if (!start) {
            throw new Error('Start time is required')
        }

        const startIso = start.toISOString()
        const endIso = end?.toISOString() ?? null
        const activityTags = this.selectedCategory ? ['shift', this.selectedCategory] : ['shift']
        const notesValue = this.notes.trim() || null

        // Include reference batch tags if jobs were selected for "Waiting on Parts"
        for (const batch of this.waitingOnPartsBatches) {
            activityTags.push(`ref_batch:${batch.batch_event_id}`)
        }

        if (endIso) {
            await firstValueFrom(
                this._opsService.fillGap(this.data.siteId, startIso, endIso, notesValue ?? '', undefined, activityTags)
            )
            return
        }

        const entry = await firstValueFrom(
            this._opsService.clockIn({
                site_id: this.data.siteId,
                clock_in_ts: startIso,
                tags: activityTags
            })
        )

        if (notesValue && entry) {
            await firstValueFrom(this._opsService.updateLaborEntry(entry.id, { notes: notesValue }))
        }
    }

    private async submitJobTime(userId: string): Promise<void> {
        const start = this.asDate(this.startDateObj)
        const end = this.asDate(this.endDateObj)

        if (!this.selectedStationId) {
            this.error = 'Station is required'
            throw new Error(this.error)
        }

        if (!start) {
            this.error = 'Start time is required'
            throw new Error(this.error)
        }

        if (this.isMultipleJobMode && this.selectedBatches.length < 2) {
            this.error = 'Select at least two jobs in Multiple Jobs mode'
            throw new Error(this.error)
        }

        // Multi-job mode: create a single entry allocated across all batches
        // end time is optional — omitting it starts an active (open) entry
        if (this.isMultipleJobMode) {
            const result = await firstValueFrom(
                submitWithReworkPrompt(this._dialog, (isRework) =>
                    this._opsService.logMultiJobEntry({
                        site_id: this.data.siteId,
                        equipment_id: this.selectedStationId!,
                        batch_event_ids: this.selectedBatches.map(batch => batch.batch_event_id),
                        clock_in_ts: start.toISOString(),
                        clock_out_ts: end ? end.toISOString() : undefined,
                        notes: this.notes || null,
                        tags: ['labor'],
                        is_rework: isRework
                    })
                )
            )
            if (!result) throw new Error('cancelled')
            return
        }

        // Single job mode: existing behavior
        const batch = this.selectedBatches[0]

        if (end) {
            const result = await firstValueFrom(
                submitWithReworkPrompt(this._dialog, (isRework) =>
                    this._opsService.logCompletedJobSlice({
                        site_id: this.data.siteId,
                        equipment_id: this.selectedStationId!,
                        source_batch_event_id: batch.batch_event_id,
                        clock_in_ts: start.toISOString(),
                        clock_out_ts: end.toISOString(),
                        notes: this.notes || null,
                        tags: ['labor'],
                        is_rework: isRework
                    })
                )
            )
            if (!result) throw new Error('cancelled')
            return
        }

        const ensuredBatch = await firstValueFrom(
            submitWithReworkPrompt(this._dialog, (isRework) =>
                this._opsService.ensureBatchAtStation({
                    equipment_id: this.selectedStationId!,
                    source_batch_event_id: batch.batch_event_id,
                    start_ts: start.toISOString(),
                    notes: this.notes || null,
                    is_rework: isRework
                })
            )
        )

        if (!ensuredBatch) throw new Error('cancelled')

        if (!ensuredBatch.created) {
            await firstValueFrom(this._oeeDataService.startBatchTimeEntry(ensuredBatch.batch_event_id, {
                user_id: userId,
                start_ts: start.toISOString(),
                tags: ['labor']
            }))
        }
    }

    private asDate(value: Date | string | null | undefined): Date | null {
        if (!value) {
            return null
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value
        }

        const parsed = new Date(value)
        return Number.isNaN(parsed.getTime()) ? null : parsed
    }

    private resolveInitialStationId(): string | null {
        const storedStationId = this.getLastStationPreference()
        if (storedStationId && this.stations.some(station => station.id === storedStationId)) {
            return storedStationId
        }

        return this.stations[0]?.id ?? null
    }

    private getLastStationPreference(): string | null {
        try {
            return localStorage.getItem(`${this.lastStationStorageKeyPrefix}:${this.data.siteId}`)
        } catch {
            return null
        }
    }

    private persistLastStationPreference(stationId: string | null): void {
        if (!stationId) {
            return
        }

        try {
            localStorage.setItem(`${this.lastStationStorageKeyPrefix}:${this.data.siteId}`, stationId)
        } catch {
            // Ignore storage failures.
        }
    }

    private extractApiErrorMessage(err: unknown, fallback: string): string {
        const apiError = typeof err === 'object' && err !== null && 'error' in err
            ? (err as { error?: unknown }).error
            : null

        if (typeof apiError === 'string' && apiError.trim()) {
            return this.normalizeApiErrorMessage(apiError)
        }

        if (typeof apiError === 'object' && apiError !== null) {
            const nestedError = 'error' in apiError ? (apiError as { error?: unknown }).error : null
            if (typeof nestedError === 'string' && nestedError.trim()) {
                return this.normalizeApiErrorMessage(nestedError)
            }

            const nestedMessage = 'message' in apiError ? (apiError as { message?: unknown }).message : null
            if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
                return this.normalizeApiErrorMessage(nestedMessage)
            }
        }

        const message = typeof err === 'object' && err !== null && 'message' in err
            ? (err as { message?: unknown }).message
            : null

        return typeof message === 'string' && message.trim()
            ? this.normalizeApiErrorMessage(message)
            : fallback
    }

    private normalizeApiErrorMessage(message: string): string {
        switch (message.trim()) {
            case 'User already has an active time entry on this batch':
                return 'You are already clocked into this job'
            default:
                return message
        }
    }

    onCancel(): void {
        this._dialogRef.close()
    }

    private applyInitialDuplicateTemplate(): void {
        if (this.duplicateTemplates.length === 0) {
            return
        }

        this.applyDuplicateTemplate(this.duplicateTemplates[0])
    }

    private toLocalDateString(date: Date): string {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
    }
}
